import { CharacterDropDown, CharacterListEntry } from '@datapad/common-controls';
import { TreeReference, type Character } from '@datapad/data-model';
import { Box, Typography } from '@mui/material';
import type React from 'react';
import { menuLabelSx, menuLabelTypographyVariant, menuSectionSx } from './Constants.js';

/**
 * {@link CharacterSelector} input props.
 */
export interface CharacterSelectorProps {
	/**
	 * The player's available characters.
	 */
	characters: readonly Character[];

	/**
	 * Reference to the currently selected character.
	 */
	currentCharacter: TreeReference | undefined;

	/**
	 * Callback when the character selection changes.
	 */
	changeSelectedCharacter(newCharacter: TreeReference | undefined): void;
}

/**
 * Datapad menu character-selection drop-down selector.
 *
 * @remarks
 * Displays either a simple character name (single character) or a dropdown
 * menu (multiple characters) for switching between player characters.
 */
export function CharacterSelector(props: CharacterSelectorProps): React.ReactElement {
	const { characters, currentCharacter: currentCharacterRef, changeSelectedCharacter } = props;

	if (characters.length === 0) {
		return (
			<Box sx={menuSectionSx}>
				<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
					No characters assigned
				</Typography>
			</Box>
		);
	}

	const currentCharacter = characters.find((character) =>
		currentCharacterRef?.isReferenceTo(character),
	);

	return (
		<Box sx={menuSectionSx}>
			<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
				Character
			</Typography>

			{/*
			 * Show a simple entry for single character, or a dropdown for multiple.
			 * This avoids unnecessary complexity when there's only one option.
			 */}
			{characters.length === 1 ? (
				<CharacterListEntry character={characters[0]} button={false} />
			) : (
				<CharacterDropDown
					characters={characters}
					selection={currentCharacter}
					changeSelection={(character) =>
						changeSelectedCharacter(TreeReference.createReferenceToNode(character))
					}
					size="small"
				/>
			)}
		</Box>
	);
}
